// register-user.component.ts
import { 
  ChangeDetectionStrategy, 
  Component, 
  CUSTOM_ELEMENTS_SCHEMA, 
  DestroyRef, 
  ElementRef, 
  inject, 
  OnDestroy, 
  OnInit, 
  Signal, 
  signal,
  viewChild 
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { provideNativeDateAdapter } from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegisterUserForm } from './interfaces';
import { Loader1Component } from '../../shared/loaders/loader-1/loader-1.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoaderCarousel } from './shared/components';
import { BreakpointsService } from '../../services/breakpoints.service';
import { RegisterUserFormDesktopComponent, RegisterUserFormMobileComponent } from './components';
import { RegisterUserFormService, RegisterUserService } from './services';
import { GeolocationRequestService } from './components/register-user-form-mobile/services';
import { GeolocationRequestComponent } from "./components/register-user-form-mobile/components";
import { RegisterUserSubmissionService } from './services/register-user-submission.service';
import { RegisterUserFormFactoryService } from './services/register-user-form-factory.service';
import { RestService } from 'src/app/services/rest.service';

@Component({
  selector: 'app-register-user',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    Loader1Component,
    MatTooltipModule,
    RegisterUserFormMobileComponent,
    RegisterUserFormDesktopComponent,
    GeolocationRequestComponent,
    LoaderCarousel
  ],
  providers: [DatePipe, provideNativeDateAdapter()],
  templateUrl: './register-user.component.html',
  styleUrls: ['./register-user.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterUserComponent implements OnInit, OnDestroy {
  // ViewChildren
  public formRef: Signal<ElementRef | undefined> = viewChild('formRef');
  public loader: Signal<ElementRef | undefined> = viewChild('loader');
  
  // Services
  private _restService = inject(RestService);
  private _registerUserService = inject(RegisterUserService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _geolocationRequestService = inject(GeolocationRequestService);
  private _breakpointsService = inject(BreakpointsService);
  private _submissionService = inject(RegisterUserSubmissionService);
  private _formFactoryService = inject(RegisterUserFormFactoryService);
  private _destroyRef = inject(DestroyRef);
  
  // State
  private _isSubmitting = signal(false);
  
  // Form
  public form: FormGroup<RegisterUserForm> = this._formFactoryService.createForm();
  
  // Public signals for template
  protected isLoadingRegisterUserData = this._registerUserService.isLoadingRegisterUserData;
  protected isLoadingFirstCustomRoute = this._registerUserService.isLoadingFirstCustomRoute;
  protected registerUser = this._registerUserService.registerUser;
  protected isMobile = this._breakpointsService.isMobile;
  protected isGeolocationView = this._geolocationRequestService.isGeolocationView;

  constructor() {
    this._initializeServices();
  }

  // Lifecycle hook
  ngOnInit(): void {
    this._submissionService.initializeComponent();
    
    // Set subdomain field
    const subdomain = this._restService.subDomain;
    this.form.get('subdomain').setValue(subdomain);
  }
  
  ngOnDestroy(): void {
    this._registerUserService.setIsLoadingFirstCustomRoute(false);
  }

  // Methods
  public handleSubmit(): void {
    if (this.form.invalid || this._isSubmitting()) {
      this.form.markAllAsTouched();
      return;
    }

    this._isSubmitting.set(true);
    
    this._submissionService.handleFormGroupSubmission(this.form)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => {
          this._isSubmitting.set(false);
          
          if (result.success && result.shouldNavigate) {
            this._submissionService.navigateToCustomRoute();
          }
        },
        error: (error) => {
          console.error('Submission error:', error);
          this._isSubmitting.set(false);
        }
      });
  }

  private _initializeServices(): void {
    this._registerUserFormService.resetForm(this.form);
    this._registerUserFormService.setMarkAllFormFieldsAsTouched(false);
    this._registerUserService.setIsLoadingFirstCustomRoute(false);
  }
}